"use client";

import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ScrollHintState,
  TimelineViewportState,
} from "./gantt-render-selectors";

const SCROLL_SYNC_SOURCE = {
  LABELS: "labels",
  TIMELINE: "timeline",
} as const;

const EMPTY_SCROLL_HINT_STATE: ScrollHintState = {
  showLeft: false,
  showRight: false,
};

const EMPTY_TIMELINE_VIEWPORT_STATE: TimelineViewportState = {
  scrollLeft: 0,
  scrollTop: 0,
  width: 0,
  height: 0,
};

interface TimelineViewportNodes {
  timelineNode: HTMLDivElement;
  labelsNode: HTMLDivElement;
}

export interface UseGanttTimelineViewportResult {
  labelsPaneRef: RefObject<HTMLDivElement | null>;
  timelineScrollRef: RefObject<HTMLDivElement | null>;
  scrollHintState: ScrollHintState;
  timelineViewportState: TimelineViewportState;
}

function selectScrollHintState(timelineNode: HTMLDivElement): ScrollHintState {
  return {
    showLeft: timelineNode.scrollLeft > 1,
    showRight:
      timelineNode.scrollLeft + timelineNode.clientWidth <
      timelineNode.scrollWidth - 1,
  };
}

function readTimelineViewportState(
  timelineNode: HTMLDivElement,
): TimelineViewportState {
  return {
    scrollLeft: timelineNode.scrollLeft,
    scrollTop: timelineNode.scrollTop,
    width: timelineNode.clientWidth,
    height: timelineNode.clientHeight,
  };
}

function areScrollHintStatesEqual({
  previous,
  next,
}: {
  previous: ScrollHintState;
  next: ScrollHintState;
}): boolean {
  return previous.showLeft === next.showLeft && previous.showRight === next.showRight;
}

function areTimelineViewportStatesEqual({
  previous,
  next,
}: {
  previous: TimelineViewportState;
  next: TimelineViewportState;
}): boolean {
  return (
    previous.scrollLeft === next.scrollLeft &&
    previous.scrollTop === next.scrollTop &&
    previous.width === next.width &&
    previous.height === next.height
  );
}

function readTimelineViewportNodes({
  timelineScrollRef,
  labelsPaneRef,
}: {
  timelineScrollRef: RefObject<HTMLDivElement | null>;
  labelsPaneRef: RefObject<HTMLDivElement | null>;
}): TimelineViewportNodes | null {
  const timelineNode = timelineScrollRef.current;
  const labelsNode = labelsPaneRef.current;
  if (!timelineNode || !labelsNode) {
    return null;
  }

  return {
    timelineNode,
    labelsNode,
  };
}

export function useGanttTimelineViewport(): UseGanttTimelineViewportResult {
  const labelsPaneRef = useRef<HTMLDivElement | null>(null);
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollHintAnimationFrameRef = useRef<number | null>(null);
  const scrollSyncSourceRef = useRef<
    (typeof SCROLL_SYNC_SOURCE)[keyof typeof SCROLL_SYNC_SOURCE] | null
  >(null);

  const [scrollHintState, setScrollHintState] = useState<ScrollHintState>(
    EMPTY_SCROLL_HINT_STATE,
  );
  const [timelineViewportState, setTimelineViewportState] =
    useState<TimelineViewportState>(EMPTY_TIMELINE_VIEWPORT_STATE);

  const updateTimelineViewportUi = useCallback((): void => {
    const timelineNode = timelineScrollRef.current;
    if (!timelineNode) {
      setScrollHintState(EMPTY_SCROLL_HINT_STATE);
      setTimelineViewportState(EMPTY_TIMELINE_VIEWPORT_STATE);
      return;
    }

    const nextScrollHintState = selectScrollHintState(timelineNode);
    setScrollHintState((previous) => {
      return areScrollHintStatesEqual({
        previous,
        next: nextScrollHintState,
      })
        ? previous
        : nextScrollHintState;
    });

    const nextTimelineViewportState = readTimelineViewportState(timelineNode);
    setTimelineViewportState((previous) => {
      return areTimelineViewportStatesEqual({
        previous,
        next: nextTimelineViewportState,
      })
        ? previous
        : nextTimelineViewportState;
    });
  }, []);

  const scheduleTimelineViewportUiUpdate = useCallback((): void => {
    if (typeof window === "undefined") {
      return;
    }

    if (scrollHintAnimationFrameRef.current != null) {
      window.cancelAnimationFrame(scrollHintAnimationFrameRef.current);
    }

    scrollHintAnimationFrameRef.current = window.requestAnimationFrame(() => {
      updateTimelineViewportUi();
    });
  }, [updateTimelineViewportUi]);

  useEffect(() => {
    const viewportNodes = readTimelineViewportNodes({
      timelineScrollRef,
      labelsPaneRef,
    });
    if (!viewportNodes) {
      return undefined;
    }

    const { timelineNode, labelsNode } = viewportNodes;
    const handleTimelineScroll = (): void => {
      if (scrollSyncSourceRef.current === SCROLL_SYNC_SOURCE.LABELS) {
        scrollSyncSourceRef.current = null;
      } else if (labelsNode.scrollTop !== timelineNode.scrollTop) {
        scrollSyncSourceRef.current = SCROLL_SYNC_SOURCE.TIMELINE;
        labelsNode.scrollTop = timelineNode.scrollTop;
      }

      scheduleTimelineViewportUiUpdate();
    };

    scheduleTimelineViewportUiUpdate();
    timelineNode.addEventListener("scroll", handleTimelineScroll, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(() => {
      scheduleTimelineViewportUiUpdate();
    });
    resizeObserver.observe(timelineNode);

    return () => {
      timelineNode.removeEventListener("scroll", handleTimelineScroll);
      resizeObserver.disconnect();

      if (scrollHintAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(scrollHintAnimationFrameRef.current);
        scrollHintAnimationFrameRef.current = null;
      }
    };
  }, [scheduleTimelineViewportUiUpdate]);

  return {
    labelsPaneRef,
    timelineScrollRef,
    scrollHintState,
    timelineViewportState,
  };
}
